"use client";

/**
 * 전역 unread 채팅 카운트 — 단일 realtime 구독 스토어.
 *
 * 배경(2026-06-12 콘서트 동시접속 장애): UserMenu와 BottomNavBar가 각자
 * chat_rooms postgres_changes 채널을 만들어 로그인 유저 1명당 전역 구독이
 * 2배로 잡혔다. Realtime은 chat_rooms에 write가 일어날 때마다 "모든 구독"에
 * 대해 SELECT RLS(정책 3개, 그중 get_my_partner_id/is_super_admin은 users
 * 조회 함수)를 평가하므로, 동시접속 N명 × write 수만큼 DB CPU를 태운다 —
 * pg_stat_statements 1위가 realtime WAL apply_rls(총 119s)였다.
 *
 * 이 스토어는 (1) 채널을 모듈 싱글톤 1개로 합치고, (2) 메시지 단위로 burst로
 * 오는 chat_rooms 이벤트를 debounce해 unread 재조회를 1회로 묶고,
 * (3) visibility/online 재연결을 한 곳에서 처리한다.
 */

import { useCallback, useSyncExternalStore } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { createClient } from "@/lib/supabase/client";
import { getTotalUnreadCount } from "@/app/actions/chat";

type Listener = () => void;

let count = 0;
let currentUserId: string | null = null;
let channel: RealtimeChannel | null = null;
const listeners = new Set<Listener>();
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
let lifecycleBound = false;

function emit() {
  for (const l of listeners) l();
}

async function reload() {
  if (!currentUserId) return;
  try {
    // WebView/Safari가 백그라운드 전환 시 in-flight Server Action POST를
    // abort시키므로(unhandled "Failed to fetch" 소음원) try/catch 필수.
    const { count: next } = await getTotalUnreadCount();
    if (next !== count) {
      count = next;
      emit();
    }
  } catch (error) {
    console.warn("[unread-chat-store] Failed to load unread count:", error);
  }
}

function scheduleReload() {
  // chat_rooms UPDATE는 메시지 1건마다 발생한다(트리거가 unread_count 갱신).
  // 연속 수신 시 마지막 이벤트 기준 800ms 한 번만 재조회.
  if (reloadTimer) clearTimeout(reloadTimer);
  reloadTimer = setTimeout(() => {
    reloadTimer = null;
    void reload();
  }, 800);
}

function teardownChannel() {
  if (channel) {
    void createClient().removeChannel(channel);
    channel = null;
  }
}

function setupChannel() {
  if (!currentUserId) return;
  teardownChannel();
  channel = createClient()
    .channel(`global-unread-${currentUserId}`)
    .on(
      "postgres_changes",
      {
        event: "UPDATE",
        schema: "public",
        table: "chat_rooms",
        filter: `customer_id=eq.${currentUserId}`,
      },
      scheduleReload,
    )
    .on(
      "postgres_changes",
      {
        event: "INSERT",
        schema: "public",
        table: "chat_rooms",
        filter: `customer_id=eq.${currentUserId}`,
      },
      scheduleReload,
    )
    .subscribe();
}

function handleWake() {
  if (!currentUserId || listeners.size === 0) return;
  void reload();
  // realtime 소켓이 백그라운드에서 끊겼을 수 있어 재구독.
  setTimeout(() => {
    if (currentUserId && listeners.size > 0) setupChannel();
  }, 500);
}

function bindLifecycle() {
  if (lifecycleBound || typeof window === "undefined") return;
  lifecycleBound = true;
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") handleWake();
  });
  window.addEventListener("online", handleWake);
}

function attach(userId: string | null, listener: Listener): () => void {
  listeners.add(listener);
  bindLifecycle();

  if (userId !== currentUserId) {
    // 로그인/로그아웃/계정 전환 — 채널을 새 유저 기준으로 교체.
    currentUserId = userId;
    count = 0;
    emit();
    if (userId) {
      setupChannel();
      void reload();
    } else {
      teardownChannel();
    }
  } else if (userId && !channel) {
    setupChannel();
    void reload();
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      teardownChannel();
      if (reloadTimer) {
        clearTimeout(reloadTimer);
        reloadTimer = null;
      }
    }
  };
}

/**
 * 로그인 유저의 전역 unread 채팅 수. 여러 컴포넌트(UserMenu, BottomNavBar)가
 * 동시에 써도 realtime 구독·재조회는 1개만 유지된다.
 */
export function useUnreadChatCount(userId: string | null | undefined): number {
  const subscribe = useCallback(
    (listener: Listener) => attach(userId ?? null, listener),
    [userId],
  );
  return useSyncExternalStore(
    subscribe,
    () => (userId ? count : 0),
    () => 0,
  );
}
